import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {ActionButton} from '../ActionButton';
import {Circle, Map, Marker, Popup, TileLayer} from 'react-leaflet';
import {InputNumber} from 'primereact/inputnumber';
import {Panel} from 'primereact/panel';
import InputTextComponent from '../inputs/InputTextComponent';
import {t} from 'i18next';
import {buildNominatimSearchParams, nominatimParamsToQueryString} from '../../utils/NominatimUtils';


export const AddressComponents = {
    CITY:"CITY",
    STREET_NAME:"STREET_NAME",
    STREET_NUMBER:"STREET_NUMBER",
    HOUSE_NUMBER:"HOUSE_NUMBER",
    POSTAL_CODE:"POSTAL_CODE",
}

export const LocationsComponent = (props) => {
    const {locations, onAddLocation, addressInfoComponents, singleLocation, onUpdateLocation, viewMode, validator, showErrorMessage, showSuccessMessage, initLocation, style} = props;
    const [zoomLevel, setZoomLevel] = useState(6);
    const [city, setCity] = useState(undefined);
    const [streetName, setStreetName] = useState(undefined);
    const [streetNumber, setStreetNumber] = useState(undefined);
    const [postalCode, setPostalCode] = useState(undefined);
    const [houseNumber, setHouseNumber] = useState(undefined);
    const [loading, setLoading] = useState(false);

    const handleZoomChange = (event) => {
        setZoomLevel(event.target.value);
    };
    const searchAddress = async (form) => {
        const queryString = nominatimParamsToQueryString(buildNominatimSearchParams(form));
        try {
            const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&${queryString}`);
            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Błąd podczas wyszukiwania adresu:', error);
            return null;
        }
    };

    const addMarker = (event) => {
        if (viewMode !== 'VIEW' ) {
            const lat = parseFloat(event.latlng.lat);
            const lng = parseFloat(event.latlng.lng);
            const precision = 18;
            const location = locations.find(
                (l) =>
                    parseFloat(l.latitude).toFixed(precision) === lat.toFixed(precision) &&
                    parseFloat(l.longitude).toFixed(precision) === lng.toFixed(precision)
            );

            if (location) {
                showErrorMessage(t("global.locationAlreadyAdded"));
                return false;
            }
            const newMarker = {
                latitude: lat,
                longitude: lng,
                localizationTolerance: 0,
                id: undefined,
            };
            onAddLocation(newMarker);
            return true;
        }
    };

    const handleMarkerDrag = (index, event) => {
        if (viewMode !== 'VIEW') {
            const {lat, lng} = event.target.getLatLng();
            const updatedMarkers = [...locations];
            updatedMarkers[index] = {
                latitude: lat,
                longitude: lng,
                localizationTolerance: updatedMarkers[index].localizationTolerance,
                id: updatedMarkers[index].id,
            };
            onUpdateLocation(updatedMarkers);
        }
    };

    const removeMarker = (index) => {
        const updatedMarkers = [...locations];
        updatedMarkers.splice(index, 1);
        onUpdateLocation(updatedMarkers);
    };

    const onToleranceChange = (index, event) => {
        const updatedMarkers = [...locations];
        updatedMarkers[index].localizationTolerance = event.value;
        onUpdateLocation(updatedMarkers);
    };

    useEffect(() => {}, [locations]);

    return (
        <div className='row'>
            <div className={`${viewMode === 'VIEW' ? 'col-12' : 'col-lg-8 col-md-6 cols-sm-12'}  `}>
                <div className='mb-4 mt-4'>
                    <Map
                        center={initLocation}
                        zoom={zoomLevel}
                        style={style}
                        onZoomEnd={handleZoomChange}
                        onClick={addMarker}
                    >
                        <TileLayer
                            url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                        />
                        {locations.map((location, index) => (
                            <Marker
                                key={index}
                                position={[location.latitude, location.longitude]}
                                draggable={viewMode !== 'VIEW'}
                                ondragend={(event) => handleMarkerDrag(index, event)}
                            >
                                <Popup>
                                    <label htmlFor='stacked-buttons' className='font-bold block mb-2'>
                                        {t("global.distanceTolerance")}
                                    </label>
                                    <InputNumber
                                        inputId='stacked-buttons'
                                        minFractionDigits={2}
                                        min={0}
                                        disabled={viewMode === 'VIEW'}
                                        label={t("global.tolerance")}
                                        className='mb-2'
                                        value={location.localizationTolerance}
                                        onChange={(e) => {
                                            onToleranceChange(index, e);
                                        }}
                                    />
                                    <br />
                                    <ActionButton
                                        rendered={viewMode !== 'VIEW'}
                                        label={t("global.remove")}
                                        handleClick={() => removeMarker(index)}
                                        className={'float-right'}
                                    />
                                </Popup>
                                <Circle
                                    center={[location.latitude, location.longitude]}
                                    radius={location?.localizationTolerance ? location?.localizationTolerance * 1000 : 0}
                                />
                            </Marker>
                        ))}
                    </Map>
                </div>
            </div>
            {viewMode !== 'VIEW' && (
                <Panel className='col-lg-4 col-md-6 cols-sm-12 mt-4 mb-4' header={t('global.address')}>
                    <div className=''>
                        <div className='row'>
                            <InputTextComponent
                                id={`city`}
                                name='city'
                                label={t('partner.city')}
                                colClass='col-lg-12 col-md-12'
                                value={city}
                                validator={validator}
                                validators='required|max:255'
                                onChange={(a, b, c, d) => {
                                    setCity(c.currentTarget?.value);
                                }}
                                rendered={!!addressInfoComponents.find(el=> el === AddressComponents.CITY)}
                                viewMode={viewMode}
                            />     
                            <InputTextComponent
                                id={`streetName`}
                                name='streetName'
                                label={t('partner.streetName')}
                                colClass='col-lg-12 col-md-12'
                                value={streetName}
                                validators='required|max:64'
                                validator={validator}
                                onChange={(a, b, c, d) => {
                                    setStreetName(c.currentTarget?.value);
                                }}
                                rendered={!!addressInfoComponents.find(el=> el === AddressComponents.STREET_NAME)}
                                viewMode={viewMode}
                            />
                            <InputTextComponent
                                id={`streetNumber`}
                                name='streetNumber'
                                label={t('partner.streetNumber')}
                                colClass='col-lg-12 col-md-12'
                                value={streetNumber}
                                validator={validator}
                                validators='required|max:64'
                                onChange={(a, b, c, d) => {
                                    setStreetNumber(c.currentTarget?.value);
                                }}
                                rendered={!!addressInfoComponents.find(el=> el === AddressComponents.STREET_NUMBER)}
                                viewMode={viewMode}
                            />
                           <InputTextComponent
                                id={`houseNumber`}
                                name='houseNumber'
                                label={t('partner.houseNumber')}
                                colClass='col-lg-12 col-md-12'
                                value={houseNumber}
                                validator={validator}
                                validators='not_required'
                                onChange={(a, b, c, d) => {
                                    setHouseNumber(c.currentTarget?.value);
                                }}
                                rendered={!!addressInfoComponents.find(el=> el === AddressComponents.HOUSE_NUMBER)}
                                viewMode={viewMode}
                            />
                            <InputTextComponent
                                id={`postalCode`}
                                name='postalCode'
                                label={t('address.postalCode')}
                                colClass='col-lg-12 col-md-12'
                                value={postalCode}
                                validators={`zipcode|required`}
                                validator={validator}
                                onChange={(a, b, c, d) => {
                                    setPostalCode(c.currentTarget?.value);
                                }}
                                rendered={!!addressInfoComponents.find(el=> el === AddressComponents.POSTAL_CODE)}
                                viewMode={viewMode}
                                validateViewMode
                            />
                        </div>
                        <ActionButton
                            id={"add-location-btn"}
                            className=''
                            rendered={viewMode !== 'VIEW'}
                            reverse
                            disabled={singleLocation && locations.length === 1}
                            label={t("global.add")}
                            handleClick={() => {
                                if (validator.allValid()) {
                                    searchAddress({
                                        country: 'Poland',
                                        city,
                                        streetName,
                                        streetNumber,
                                        postalCode,
                                        houseNumber,
                                    })
                                        .then((results) => {
                                            if (results.length > 0) {
                                                const lat = results[0].lat;
                                                const lng = results[0].lon;
                                                const fakeEvent = {
                                                    latlng: {
                                                        lat: lat,
                                                        lng: lng,
                                                    },
                                                };
                                                const successful = addMarker(fakeEvent);
                                                if (successful) {
                                                    setCity('');
                                                    setStreetName('');
                                                    setStreetNumber('');
                                                    setPostalCode('');
                                                    setHouseNumber('');
                                                    showSuccessMessage(t("global.locationSuccessfulAdded"));
                                                }
                                                return;
                                            }
                                            showErrorMessage(t('global.locationNotFound'));
                                        })
                                        .catch((ex) => {
                                            showErrorMessage(ex);
                                        });
                                } else {
                                    validator.showMessages();
                                    setLoading(!loading);
                                }
                            }}
                        />
                    </div>
                </Panel>
            )}
        </div>
    );
};

LocationsComponent.defaultProps = {
    viewMode: 'VIEW',
    locations: [],
    style:{height: '500px', zIndex: '100'},            
    singleLocation: false,
    initLocation: [52.237, 21.017],
    addressInfoComponents:[AddressComponents.CITY, AddressComponents.STREET_NAME,AddressComponents.POSTAL_CODE, AddressComponents.STREET_NUMBER,AddressComponents.HOUSE_NUMBER ]
};

LocationsComponent.propTypes = {
    viewMode: PropTypes.string,
    initLocation:PropTypes.array,
    singleLocation: PropTypes.bool,
    onAddLocation: PropTypes.func.isRequired,
    showErrorMessage: PropTypes.func.isRequired,
    validator: PropTypes.object.isRequired,
    style: PropTypes.object,
    onUpdateLocation: PropTypes.func.isRequired,
    locations: PropTypes.array,
    addressInfoComponents: PropTypes.array,
};

export default LocationsComponent;
